import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { PropertyInvitationDetailsDto, PropertyMemberRole } from '@workspace/data-types';
import { environment } from '@workspace/ng-config';
import { LocalStorageKey, OpenIdService, UserStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { EMPTY, catchError, finalize, take, tap } from 'rxjs';

export type InvitationPageState = 'loading' | 'inspect' | 'accepted' | 'declined' | 'error';

@Component({
  selector: 'etxe-property-invitation-page',
  standalone: true,
  imports: [CommonModule, RouterModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './property-invitation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyInvitationPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(ApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userStore = inject(UserStore);
  private readonly openId = inject(OpenIdService, { optional: true });

  readonly pageState = signal<InvitationPageState>('loading');
  readonly isSubmitting = signal<boolean>(false);
  readonly token = signal<string>('');
  readonly invitation = signal<PropertyInvitationDetailsDto | null>(null);
  readonly errorMessage = signal<string>('');

  readonly currentUser = computed(() => this.userStore.current());
  readonly isLoggedIn = computed(() => !!this.currentUser());

  readonly isMatchingEmail = computed(() => {
    const inv = this.invitation();
    const user = this.currentUser();
    if (!inv || !user?.contactInfos?.email) return false;
    return user.contactInfos.email.toLowerCase() === inv.email.toLowerCase();
  });

  readonly roleLabel = computed(() => {
    const role = this.invitation()?.role;
    if (role === PropertyMemberRole.VIEWER) {
      return 'Spectateur';
    }
    return 'Co-vendeur';
  });

  ngOnInit(): void {
    this.route.queryParams
      .pipe(
        take(1),
        tap((params) => {
          const token = params['token'];
          if (!token) {
            this.errorMessage.set("Le lien d'invitation est incomplet ou invalide.");
            this.pageState.set('error');
            return;
          }
          this.token.set(token);
          this.loadInvitationDetails(token);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  loadInvitationDetails(token: string): void {
    this.pageState.set('loading');
    this.api
      .business()
      .etxe()
      .public()
      .properties()
      .getInvitationDetails(token)
      .pipe(
        tap((details) => {
          this.invitation.set(details);
          this.pageState.set('inspect');
        }),
        catchError((err) => {
          this.errorMessage.set(
            err?.error?.message || "Cette invitation est introuvable, a expiré ou n'est plus active.",
          );
          this.pageState.set('error');
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onSignUp(): void {
    const inv = this.invitation();
    const token = this.token();
    const currentUrl = window.location.pathname + window.location.search;
    localStorage.setItem(LocalStorageKey.AUTH_RETURN_URL, currentUrl);
    if (token) {
      localStorage.setItem(LocalStorageKey.INVITATION_TOKEN, token);
    }
    if (inv?.email) {
      localStorage.setItem(LocalStorageKey.SIGN_UP_EMAIL, inv.email);
    }
    void this.router.navigate(['/auth/sign-up'], {
      queryParams: token ? { token } : {},
    });
  }

  onLogin(): void {
    const inv = this.invitation();
    const currentUrl = window.location.pathname + window.location.search;
    this.openId?.login(inv?.email, currentUrl);
  }

  onSwitchAccount(): void {
    const inv = this.invitation();
    const currentUrl = window.location.pathname + window.location.search;
    localStorage.setItem(LocalStorageKey.AUTH_RETURN_URL, currentUrl);
    this.openId?.logout(false);
    this.openId?.login(inv?.email, currentUrl);
  }

  onAccept(): void {
    const t = this.token();
    if (!t) return;

    this.isSubmitting.set(true);
    this.api
      .business()
      .etxe()
      .properties()
      .owner()
      .acceptMemberInvitation(t)
      .pipe(
        tap(() => {
          this.pageState.set('accepted');
        }),
        catchError((err) => {
          this.errorMessage.set(
            err?.error?.message || "Une erreur est survenue lors de l'acceptation de l'invitation.",
          );
          this.pageState.set('error');
          return EMPTY;
        }),
        finalize(() => this.isSubmitting.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onDecline(): void {
    const t = this.token();
    if (!t) return;

    this.isSubmitting.set(true);
    this.api
      .business()
      .etxe()
      .properties()
      .owner()
      .declineMemberInvitation(t)
      .pipe(
        tap(() => {
          this.pageState.set('declined');
        }),
        catchError((err) => {
          this.errorMessage.set(
            err?.error?.message || "Une erreur est survenue lors du refus de l'invitation.",
          );
          this.pageState.set('error');
          return EMPTY;
        }),
        finalize(() => this.isSubmitting.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  goToProperty(): void {
    const inv = this.invitation();
    const ownerUrl = environment.apps.ownerUrl;
    if (inv?.property?._id) {
      window.location.href = `${ownerUrl}/properties/${inv.property._id}`;
    } else {
      window.location.href = `${ownerUrl}/properties`;
    }
  }

  goToDashboard(): void {
    const ownerUrl = environment.apps.ownerUrl;
    window.location.href = `${ownerUrl}/properties`;
  }

  goToHome(): void {
    void this.router.navigate(['/']);
  }
}
